<template>
  <Teleport to="body">
    <Transition name="lm">
      <div v-if="isVisible" class="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div class="absolute inset-0 bg-black/40" @click="close"></div>

        <div class="relative bg-white border border-zinc-100 rounded-2xl w-full max-w-sm overflow-hidden">
          <!-- Close -->
          <button
            @click="close"
            class="absolute top-4 right-4 text-zinc-400 hover:text-zinc-600 transition-colors"
            aria-label="Close"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>

          <div class="px-8 pt-8 pb-2 text-center">
            <p class="text-xs font-semibold uppercase tracking-widest text-gold mb-2">Welcome back</p>
            <h2 class="text-xl font-bold text-zinc-900 tracking-tight">Log in to your account</h2>
          </div>

          <form @submit.prevent="handleSubmit" class="px-8 pt-6 pb-8 flex flex-col gap-4">
            <!-- Error -->
            <div v-if="error" class="bg-red-50 border border-red-100 rounded-lg px-4 py-3">
              <p class="text-xs text-red-700">{{ error }}</p>
              <p v-if="showResendConfirmation" class="text-xs text-red-600 mt-1">
                <span class="underline cursor-pointer" @click="resendConfirmation">Resend confirmation email</span>
              </p>
            </div>

            <div>
              <label class="block text-xs font-medium text-zinc-500 mb-1">Email</label>
              <input
                v-model="email"
                type="email"
                required
                autocomplete="email"
                placeholder="you@example.com"
                class="w-full px-3 py-2 text-sm border border-zinc-200 rounded-lg bg-white text-zinc-900 placeholder-zinc-300 focus:outline-none focus:border-primary transition-colors"
              />
            </div>

            <div>
              <div class="flex items-center justify-between mb-1">
                <label class="block text-xs font-medium text-zinc-500">Password</label>
                <a href="/password/new" class="text-xs text-zinc-400 hover:text-primary transition-colors">Forgot password?</a>
              </div>
              <input
                v-model="password"
                type="password"
                required
                autocomplete="current-password"
                placeholder="••••••••"
                class="w-full px-3 py-2 text-sm border border-zinc-200 rounded-lg bg-white text-zinc-900 placeholder-zinc-300 focus:outline-none focus:border-primary transition-colors"
              />
            </div>

            <button
              type="submit"
              :disabled="isLoading"
              class="w-full py-2.5 rounded-lg text-sm font-semibold text-white bg-primary hover:opacity-90 transition-opacity disabled:opacity-50 mt-1"
            >
              <span v-if="isLoading" class="flex items-center justify-center gap-2">
                <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
                Signing in…
              </span>
              <span v-else>Sign in</span>
            </button>

            <p class="text-center text-xs text-zinc-400">
              No account yet?
              <button type="button" @click="switchToSignup" class="text-primary font-semibold hover:opacity-80 transition-opacity">Get started for free</button>
            </p>
          </form>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuth } from '../composables/useAuth.js'

const props = defineProps({
  isVisible: { type: Boolean, default: false }
})
const emit = defineEmits(['close', 'switch-to-signup'])

const router = useRouter()
const { invalidateAuthCache } = useAuth()

const email = ref('')
const password = ref('')
const isLoading = ref(false)
const error = ref('')
const showResendConfirmation = ref(false)
const unconfirmedEmail = ref('')

watch(() => props.isVisible, (visible) => {
  if (visible) {
    email.value = ''
    password.value = ''
    error.value = ''
    showResendConfirmation.value = false
  }
})

const close = () => {
  if (isLoading.value) return
  emit('close')
}

const switchToSignup = () => {
  emit('close')
  emit('switch-to-signup')
}

const handleSubmit = async () => {
  try {
    isLoading.value = true
    error.value = ''

    const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    const response = await fetch('/api/users/signin', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
      body: JSON.stringify({ user: { email: email.value, password: password.value } })
    })

    const data = await response.json()

    if (data.success) {
      invalidateAuthCache()
      emit('close')
      router.push('/dashboard')
    } else {
      error.value = data.message || 'Invalid email or password'
      if (data.requires_confirmation) {
        showResendConfirmation.value = true
        unconfirmedEmail.value = data.user_email || email.value
      }
    }
  } catch {
    error.value = 'An error occurred. Please try again.'
  } finally {
    isLoading.value = false
  }
}

const resendConfirmation = async () => {
  if (!unconfirmedEmail.value) return
  try {
    isLoading.value = true
    const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    const response = await fetch('/api/users/resend_confirmation', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
      body: JSON.stringify({ email: unconfirmedEmail.value })
    })
    const data = await response.json()
    if (data.success) {
      error.value = 'Confirmation email sent — check your inbox.'
      showResendConfirmation.value = false
    } else {
      error.value = data.message || 'Failed to resend confirmation email'
    }
  } catch {
    error.value = 'An error occurred. Please try again.'
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.lm-enter-active, .lm-leave-active { transition: opacity 0.15s ease; }
.lm-enter-from, .lm-leave-to { opacity: 0; }
</style>
